import React, { useEffect, useMemo } from 'react';
import { TBaseTagContext } from '../types';
import useContexts from '../../hooks/useContexts';
import { isFunction, omit } from 'lodash';
import UniversalChildren from './UniversalChildren';
import HTMLTag from '../HTMLTag';
import BootstrapTag from '../BootstrapTag';
import CustomTag from '../CustomTag';
import mutator from '@krugom/mutator';
import { TBaseTag } from '../CustomTag/types';

export const UniversalTag: React.FC<{
  tagProps: TBaseTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const mutatorContext = useContexts(tagProps.schema?.contextItems || [], context);

  const mutantProps = useMemo(
    () =>
      mutator.mutate.byScheme(tagProps, mutatorContext, tagProps.schema?.paths || []) as TBaseTag,
    [mutatorContext, tagProps]
  );

  useEffect(() => {
    const onLoad = mutantProps.excluded ? undefined : mutantProps.onLoad;
    if (isFunction(onLoad)) onLoad();
  }, [mutantProps.excluded, mutantProps.onLoad]);

  const tagName = tagProps.tag;

  const newContext = useMemo(
    () => ({
      ...context,
      variables: { ...context.variables, ...mutantProps.variables },
    }),
    [context, mutantProps.variables]
  );

  const filteredTagProps = useMemo(
    () =>
      omit(mutantProps, [
        'tag',
        'schema',
        'mutationExcludes',
        'children',
        'variables',
      ]) as Partial<TBaseTag>,
    [mutantProps]
  );

  const customTag = /^\$[A-Z][A-Za-z.]+$/.test(tagName) ? (
    <CustomTag
      name={tagName.slice(1)}
      tagProps={mutantProps}
      context={newContext}
      tagChildren={mutantProps.children}
    />
  ) : null;

  const childElements = useMemo(
    () =>
      mutantProps.children ? (
        <UniversalChildren context={newContext}>{mutantProps.children}</UniversalChildren>
      ) : null,
    [mutantProps.children, newContext]
  );

  const bootstrapTag = /^[A-Z][A-Za-z.]+$/.test(tagName) ? (
    <BootstrapTag name={tagName} tagProps={filteredTagProps}>
      {childElements}
    </BootstrapTag>
  ) : null;

  const htmlTag = /^[a-z0-9]+$/.test(tagName) ? (
    <HTMLTag name={tagName} tagProps={filteredTagProps}>
      {childElements}
    </HTMLTag>
  ) : null;

  const tag = customTag ?? bootstrapTag ?? htmlTag;

  if (!tag) {
    console.error(`Unknown tag "${tagName}"`);
    return <UniversalChildren context={newContext}>{mutantProps.children}</UniversalChildren>;
  }

  return <>{tag}</>;
});

UniversalTag.displayName = 'UniversalTag';
